import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  Divider,
  alpha,
  styled
} from '@mui/material';
import {
  Person,
  Email,
  Key,
  Save
} from '@mui/icons-material';

// Matching styles from TenantUsers.js
const COLORS = {
  primary: '#2196f3',
  success: '#4caf50',
  warning: '#ffc107',
  purple: '#673ab7',
  error: '#f44336',
  background: '#f7f9fc',
  lightBorder: '#e3f2fd',
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius,
  },
  '& .MuiDialogTitle-root': {
    backgroundColor: alpha(COLORS.primary, 0.05),
    padding: theme.spacing(2),
    fontWeight: 600,
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
    backgroundColor: alpha(COLORS.background, 0.5),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.05), 0px 1px 10px 0px rgba(0,0,0,0.025)',
  padding: '8px 16px',
  fontWeight: 500,
}));

function UserProfileDialog({ open, onClose, profile, updateUserProfile }) {
    console.log(profile);
  const [formData, setFormData] = useState({
    email: profile?.email || '',
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    // Validate form whenever formData changes
    validateForm();
  }, [formData]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    
    // Clear error when field is changed
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate personal info
    if (!formData.first_name) newErrors.first_name = 'El nombre es requerido';
    if (!formData.last_name) newErrors.last_name = 'El apellido es requerido';
    if (!formData.email) newErrors.email = 'El email es requerido';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email inválido';
    
    // Password validation only if the user is trying to change it
    if (formData.new_password) {
      if (!formData.current_password) {
        newErrors.current_password = 'Se requiere la contraseña actual para cambiar a una nueva';
      }
      
      // Enhanced password validation
      const hasMinLength = formData.new_password.length >= 8;
      const hasUpperCase = /[A-Z]/.test(formData.new_password);
      const hasNumber = /[0-9]/.test(formData.new_password);
      const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.new_password);
      
      if (!hasMinLength || !hasUpperCase || !hasNumber || !hasSymbol) {
        newErrors.new_password = 'La contraseña debe tener al menos 8 caracteres, una mayúscula, un número y un símbolo';
      }
      
      if (formData.new_password !== formData.confirm_password) {
        newErrors.confirm_password = 'Las contraseñas no coinciden';
      }
    }
    
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    setIsFormValid(isValid);
    return isValid;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const userData = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
      };
      
      // Only include password data if user is changing password
      if (formData.new_password) {
        userData.current_password = formData.current_password;
        userData.new_password = formData.new_password;
      }
      
      updateUserProfile(userData);
      onClose();
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Editar Perfil de Usuario
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3, mt: 1 }}>
          <Typography variant="subtitle1" fontWeight={500} color="primary" gutterBottom>
            Información Personal
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            name="email"
            label="Email"
            type="email"
            variant="outlined"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="primary" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="dense"
            name="first_name"
            label="Nombre"
            type="text"
            variant="outlined"
            value={formData.first_name}
            onChange={handleChange}
            error={!!errors.first_name}
            helperText={errors.first_name}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="primary" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="dense"
            name="last_name"
            label="Apellido"
            type="text"
            variant="outlined"
            value={formData.last_name}
            onChange={handleChange}
            error={!!errors.last_name}
            helperText={errors.last_name}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={500} color="primary" gutterBottom>
            Cambiar Contraseña (opcional)
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            name="current_password"
            label="Contraseña Actual"
            type="password"
            variant="outlined"
            value={formData.current_password}
            onChange={handleChange}
            error={!!errors.current_password}
            helperText={errors.current_password}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Key color="primary" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="dense"
            name="new_password"
            label="Nueva Contraseña"
            type="password"
            variant="outlined"
            value={formData.new_password}
            onChange={handleChange}
            error={!!errors.new_password}
            helperText={errors.new_password || "Deje en blanco si no desea cambiar su contraseña"}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Key color="primary" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="dense"
            name="confirm_password"
            label="Confirmar Nueva Contraseña"
            type="password"
            variant="outlined"
            value={formData.confirm_password}
            onChange={handleChange}
            error={!!errors.confirm_password}
            helperText={errors.confirm_password}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Key color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          sx={{ 
            color: alpha('#424242', 0.8),
            '&:hover': {
              backgroundColor: alpha('#424242', 0.05),
            }
          }}
        >
          Cancelar
        </Button>
        <StyledButton 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          startIcon={<Save />}
          disabled={!isFormValid}
        >
          Guardar Cambios
        </StyledButton>
      </DialogActions>
    </StyledDialog>
  );
}

export default UserProfileDialog; 